import { useMemo, useState, type FormEvent } from 'react';
import type { Role, Viewer } from '@sfc/sdk';
import { initials } from '../ui/format';
import { Icon } from '../ui/Icon';
import { StatStrip } from '../ui/StatStrip';
import { describe, useMembers } from './useMembers';

const ROLES: readonly Role[] = ['owner', 'admin', 'editor', 'recruiter'];

interface StaffPanelProps {
  viewer: Viewer;
  /** Called after anything that changes staff, so the history can catch up. */
  onChanged: () => void;
}

/**
 * Staff administration.
 *
 * What is offered here follows the viewer's capabilities, so somebody who may
 * read the list but not change it sees a list and no controls. The API checks
 * the same rules again; this only decides what is worth showing.
 */
export function StaffPanel({ viewer, onChanged }: StaffPanelProps): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const { members, loading, problem, provision, changeRole, revoke, resetPassword } = useMembers(organizationId);
  const mayWrite = viewer.capabilities.includes('members:write');
  const mayManageOwners = viewer.capabilities.includes('members:write:owner');

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [issued, setIssued] = useState<
    { email: string; passphrase: string; reason: 'created' | 'reset'; sessionsEnded?: number } | undefined
  >();
  /**
   * Said when an address turns out to already have an account.
   *
   * Without it the screen after adding an existing person is identical to the
   * screen after adding a new one whose passphrase failed to render, and the
   * natural next move -- remove them and add them again -- produces exactly the
   * same nothing, because the account outlives the membership.
   */
  const [existing, setExisting] = useState<{ email: string; membershipId: string } | undefined>();
  const [confirmingReset, setConfirmingReset] = useState<string | undefined>();
  const [actionProblem, setActionProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const stats = useMemo(() => {
    const owners = members.filter((member) => member.role === 'owner').length;
    return [
      { label: 'Members', value: members.length },
      { label: 'Owners', value: owners, tone: owners === 1 ? ('pending' as const) : undefined },
      { label: 'Admins', value: members.filter((member) => member.role === 'admin').length },
      { label: 'Editors', value: members.filter((member) => member.role === 'editor').length },
    ];
  }, [members]);

  async function addMember(event: FormEvent): Promise<void> {
    event.preventDefault();
    setActionProblem(undefined);
    setIssued(undefined);
    setExisting(undefined);
    setBusy(true);
    try {
      const result = await provision({ email, displayName, role });
      if (result.passphrase) setIssued({ email, passphrase: result.passphrase, reason: 'created' });
      else if (result.accountExisted) setExisting({ email, membershipId: result.member.membershipId });
      setEmail('');
      setDisplayName('');
      onChanged();
    } catch (error) {
      setActionProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }

  async function issueNewPassphrase(membershipId: string, memberEmail: string): Promise<void> {
    setActionProblem(undefined);
    setIssued(undefined);
    setExisting(undefined);
    setConfirmingReset(undefined);
    try {
      const result = await resetPassword(membershipId);
      setIssued({
        email: memberEmail,
        passphrase: result.passphrase,
        reason: 'reset',
        sessionsEnded: result.sessionsEnded,
      });
      onChanged();
    } catch (error) {
      setActionProblem(describe(error));
    }
  }

  async function attempt(work: () => Promise<void>): Promise<void> {
    setActionProblem(undefined);
    try {
      await work();
      onChanged();
    } catch (error) {
      setActionProblem(describe(error));
    }
  }

  return (
    <section className="card" aria-labelledby="section-heading">
      <StatStrip stats={stats} testId="staff-stats" />

      {problem ? (
        <p className="notice notice--problem" role="alert">
          {problem}
        </p>
      ) : null}

      <div className="panel">
        <div className="panel__head">
          <h2>Members</h2>
          <span className="count" data-testid="member-count">
            {loading ? '…' : `${members.length}`}
          </span>
        </div>

        <ul className="ledger" data-testid="members">
          {members.map((member) => (
            <li key={member.membershipId} className="ledger__row ledger__row--member" data-testid={`member-${member.email}`}>
              <span className="avatar" aria-hidden="true">
                {initials(member.displayName)}
              </span>
              <div className="ledger__id">
                <span className="row__title">
                  {member.displayName}
                  {member.email === viewer.user.email ? <span className="you">You</span> : null}
                </span>
                <span className="row__sub">{member.email}</span>
              </div>

              <span className="button-row">
                {mayWrite ? (
                  <select
                    className="select select--small"
                    aria-label={`Role for ${member.email}`}
                    value={member.role}
                    onChange={(event) =>
                      void attempt(() => changeRole(member.membershipId, event.target.value as Role))
                    }
                  >
                    {ROLES.filter((option) => mayManageOwners || option !== 'owner' || member.role === 'owner').map(
                      (option) => (
                        <option key={option} value={option} disabled={option === 'owner' && !mayManageOwners}>
                          {option}
                        </option>
                      ),
                    )}
                  </select>
                ) : (
                  <span className="pill pill--pending">{member.role}</span>
                )}

                {/* Resetting an owner's password is a way to become that
                    owner, so it needs the capability that guards the owner role
                    rather than the one that guards ordinary staff changes. The
                    API refuses it again either way. */}
                {mayWrite && (member.role !== 'owner' || mayManageOwners) ? (
                  confirmingReset === member.membershipId ? (
                    <>
                      <button
                        type="button"
                        className="button button--small"
                        onClick={() => setConfirmingReset(undefined)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="button button--small button--danger"
                        aria-label={`Issue a new passphrase for ${member.email}`}
                        onClick={() => void issueNewPassphrase(member.membershipId, member.email)}
                      >
                        Issue and sign them out
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="button button--small"
                      aria-label={`Reset password for ${member.email}`}
                      onClick={() => setConfirmingReset(member.membershipId)}
                    >
                      Reset password
                    </button>
                  )
                ) : null}

                {mayWrite ? (
                  <button
                    type="button"
                    className="button button--small button--danger"
                    aria-label={`Remove ${member.email}`}
                    onClick={() => void attempt(() => revoke(member.membershipId))}
                  >
                    Remove
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {actionProblem ? (
        <p className="notice notice--problem" role="alert" data-testid="staff-problem">
          {actionProblem}
        </p>
      ) : null}

      {issued ? (
        <div className="notice notice--once" data-testid="issued-passphrase">
          <Icon name="lock" size={18} />
          <p>
            Passphrase for {issued.email}: <code>{issued.passphrase}</code>. Hand it over directly. It cannot be
            shown again — but you can issue another one from this list, so a lost passphrase is not a lost
            account.
            {issued.reason === 'reset' ? (
              <>
                {' '}
                Their old password no longer works
                {issued.sessionsEnded ? ` and ${issued.sessionsEnded} open session${
                  issued.sessionsEnded === 1 ? ' was' : 's were'
                } signed out` : ''}
                . They can set their own under Your account once they are in.
              </>
            ) : (
              ' They can set their own under Your account once they are in.'
            )}
          </p>
        </div>
      ) : null}

      {existing ? (
        <div className="notice notice--once" data-testid="existing-account">
          <Icon name="lock" size={18} />
          <p>
            {existing.email} already had an account here, so it keeps the password it has and no new passphrase
            was issued. Removing somebody ends their membership, not their account, so adding them back finds it
            again. If they need a password, use <strong>Reset password</strong> on their row above.
          </p>
        </div>
      ) : null}

      {mayWrite ? (
        <form className="panel" onSubmit={addMember} aria-labelledby="add-member-heading">
          <div className="panel__head">
            <div>
              <h2 id="add-member-heading">Add a member</h2>
              <p className="panel__hint">
                A new address gets a one-time passphrase to sign in with, which they can then change themselves.
                An address that already has an account keeps its password. Nobody can sign up on their own.
              </p>
            </div>
          </div>
          <div className="inline-form">
            <div className="field field--grow">
              <label htmlFor="member-email">Email</label>
              <input
                id="member-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="field field--grow">
              <label htmlFor="member-name">Name</label>
              <input
                id="member-name"
                type="text"
                required
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="member-role">Role</label>
              <select id="member-role" value={role} onChange={(event) => setRole(event.target.value as Role)}>
                {ROLES.filter((option) => mayManageOwners || option !== 'owner').map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? 'Adding…' : 'Add member'}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
